import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TopNav } from '../components/TopNav';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
import { SkeletonRow } from '../components/SkeletonRow';
import { EmptyState } from '../components/EmptyState';
import { RecordSendModal } from '../modals/RecordSendModal';
import { listSendBatches, downloadSentFile, deleteSendBatch } from '../lib/api';
import { fmt } from '../lib/format';
import type { SendBatch } from '../types';

/**
 * The sent-to-finance ledger: one row per recorded send (file upload or pasted
 * ID list). This is the master source of truth of what was shared with Finance.
 */
export function Sends(): JSX.Element {
  const qc = useQueryClient();
  const { data: batches, isLoading } = useQuery({ queryKey: ['send-batches'], queryFn: listSendBatches });
  const [showRecord, setShowRecord] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SendBatch | null>(null);
  const [deleting, setDeleting] = useState(false);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['send-batches'] });
    qc.invalidateQueries({ queryKey: ['report-rows'] });
  }

  return (
    <div className="ari-app">
      <TopNav />
      <div className="page">
        <PageHeader
          title="Sent to finance"
          sub="Every send recorded against the master ledger. New reports badge each session against what finance already received."
          actions={
            <>
              <Button variant="secondary" icon="refresh" onClick={() => qc.invalidateQueries({ queryKey: ['send-batches'] })}>Refresh</Button>
              <Button variant="primary" icon="arrowUp" onClick={() => setShowRecord(true)}>Record send</Button>
            </>
          }
        />

        <div style={{ flex: 1, overflow: 'auto', background: '#fff' }}>
          {isLoading ? (
            <table className="tbl gen">
              <thead><tr><th>Sent</th><th>Recorded by</th><th>Source</th><th>File</th><th className="num">IDs</th><th className="num">New</th><th className="num">Resent</th><th className="num">Unmatched</th><th /></tr></thead>
              <tbody>{Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} cells={9} />)}</tbody>
            </table>
          ) : (batches?.length ?? 0) === 0 ? (
            <EmptyState
              icon="arrowUp"
              title="No sends recorded yet"
              body="After emailing a report to finance, record it here (upload the exact file you sent, or paste the session IDs finance confirmed). You can backfill history by uploading past files."
              cta={<Button variant="primary" icon="arrowUp" onClick={() => setShowRecord(true)}>Record send</Button>}
            />
          ) : (
            <table className="tbl gen">
              <thead>
                <tr>
                  <th>Sent <span className="sortarr">↓</span></th>
                  <th>Recorded by</th>
                  <th>Source</th>
                  <th>File</th>
                  <th className="num">IDs</th>
                  <th className="num">New</th>
                  <th className="num">Resent</th>
                  <th className="num">Unmatched</th>
                  <th style={{ width: 1 }} />
                </tr>
              </thead>
              <tbody>
                {(batches ?? []).map((b) => (
                  <tr key={b.id}>
                    <td className="d-tnum">{fmt.dateTime(b.sentAt)}</td>
                    <td>{b.recordedByEmail ?? <span className="em">—</span>}</td>
                    <td>
                      <span className={`pill ${b.source === 'upload' ? 'sent' : 'paste'}`}>
                        <span className="pdot" />{b.source === 'upload' ? 'File upload' : 'Pasted IDs'}
                      </span>
                    </td>
                    <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={b.originalFilename ?? undefined}>
                      {b.originalFilename ?? <span className="em">—</span>}
                      {b.note && <div style={{ fontSize: 11, color: 'var(--mute)' }}>{b.note}</div>}
                    </td>
                    <td className="num">{fmt.num(b.sessionCount)}</td>
                    <td className="num">{b.newCount != null ? fmt.num(b.newCount) : <span className="em">—</span>}</td>
                    <td className="num">{b.resentCount != null ? fmt.num(b.resentCount) : <span className="em">—</span>}</td>
                    <td className="num">{b.unmatchedCount ? fmt.num(b.unmatchedCount) : <span className="em">—</span>}</td>
                    <td style={{ paddingRight: 16 }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        {b.storagePath && (
                          <Button size="sm" variant="ghost" icon="download" onClick={() => downloadSentFile(b.id)}>.xlsx</Button>
                        )}
                        <Button size="sm" variant="ghost" icon="trash" onClick={() => setPendingDelete(b)}>{''}</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showRecord && (
        <RecordSendModal onClose={() => setShowRecord(false)} onRecorded={invalidate} />
      )}
      {pendingDelete && (
        <Modal onClose={() => setPendingDelete(null)}>
          <div className="modal-hd">
            <div className="eb" style={{ color: 'var(--risk)' }}>Undo send record</div>
            <div className="ttl">Delete this send from the ledger?</div>
          </div>
          <div className="modal-bd">
            <p style={{ fontSize: 14, color: 'var(--ink)', lineHeight: 1.5 }}>
              Removes the record{pendingDelete.storagePath ? ' and its stored file' : ''} and recomputes the
              Sent/Unsent badges from the remaining sends. Sessions only recorded by this send go back to{' '}
              <strong>Unsent</strong>.
            </p>
            <div className="banner warn">
              <span className="dot" />
              <span>
                {pendingDelete.sessionCount.toLocaleString()} session IDs · {pendingDelete.source === 'upload' ? (pendingDelete.originalFilename ?? 'file upload') : 'pasted IDs'} · {fmt.dateTime(pendingDelete.sentAt)}
              </span>
            </div>
          </div>
          <div className="modal-foot">
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button
              variant="destructive"
              icon="trash"
              disabled={deleting}
              onClick={async () => {
                setDeleting(true);
                try {
                  await deleteSendBatch(pendingDelete.id);
                  setPendingDelete(null);
                  invalidate();
                } finally {
                  setDeleting(false);
                }
              }}
            >
              {deleting ? <><span className="spinner" />Deleting…</> : 'Delete send record'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
